import { Card } from '../ui/card'
import { Badge } from '../ui/badge'
import { LoadingSpinner } from '../ui/loading-states'
import { useDeliveryStats } from '../../hooks/useNotifications'
import {
  getDeliveryStatusLabel,
  getNotificationChannelLabel,
  DeliveryStatsResponse,
  DeliveryStatus,
  NotificationChannel,
} from '@tco/shared-types'

interface DeliveryStatsProps {
  notificationId: string
  compact?: boolean
}

export function DeliveryStats({ notificationId, compact = false }: DeliveryStatsProps) {
  const { data: stats, isLoading, isError, error } = useDeliveryStats(notificationId)

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <LoadingSpinner size="md" />
        </div>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="p-6">
        <p className="text-sm text-destructive">
          Ошибка загрузки статистики: {error instanceof Error ? error.message : 'Неизвестная ошибка'}
        </p>
      </Card>
    )
  }

  if (!stats) {
    return (
      <Card className="p-6">
        <p className="text-sm text-muted-foreground">Статистика недоступна</p>
      </Card>
    )
  }

  if (compact) {
    return <CompactStats stats={stats} />
  }

  return <FullStats stats={stats} />
}

function CompactStats({ stats }: { stats: DeliveryStatsResponse }) {
  return (
    <div className="flex items-center gap-4 text-sm">
      <div>
        <span className="text-muted-foreground">Отправлено:</span>{' '}
        <span className="font-medium">{stats.total_deliveries}</span>
      </div>
      <div>
        <span className="text-muted-foreground">Доставлено:</span>{' '}
        <span className="font-medium text-green-600">{stats.by_status.delivered}</span>
      </div>
      <div>
        <span className="text-muted-foreground">Прочитано:</span>{' '}
        <span className="font-medium text-purple-600">{stats.by_status.read}</span>
      </div>
      {stats.by_status.failed > 0 && (
        <div>
          <span className="text-muted-foreground">Ошибки:</span>{' '}
          <span className="font-medium text-destructive">{stats.by_status.failed}</span>
        </div>
      )}
    </div>
  )
}

function FullStats({ stats }: { stats: DeliveryStatsResponse }) {
  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4">Статистика доставки</h3>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-muted rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Всего отправлено</p>
          <p className="text-2xl font-bold">{stats.total_deliveries}</p>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-sm text-green-700 mb-1">Доставлено</p>
          <p className="text-2xl font-bold text-green-700">
            {stats.by_status.delivered}
          </p>
          <p className="text-xs text-green-600 mt-1">
            {stats.success_rate.toFixed(1)}% успешно
          </p>
        </div>

        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-sm text-purple-700 mb-1">Прочитано</p>
          <p className="text-2xl font-bold text-purple-700">
            {stats.by_status.read}
          </p>
          <p className="text-xs text-purple-600 mt-1">
            {stats.read_rate.toFixed(1)}% открыто
          </p>
        </div>

        {stats.by_status.failed > 0 && (
          <div className="bg-red-50 rounded-lg p-4">
            <p className="text-sm text-red-700 mb-1">Ошибки</p>
            <p className="text-2xl font-bold text-red-700">
              {stats.by_status.failed}
            </p>
          </div>
        )}
      </div>

      {/* Status Breakdown */}
      <div className="mb-6">
        <h4 className="text-sm font-medium mb-3">По статусам</h4>
        <div className="space-y-2">
          {stats.by_status.pending > 0 && (
            <StatusRow
              label={getDeliveryStatusLabel(DeliveryStatus.PENDING)}
              count={stats.by_status.pending}
              color="gray"
            />
          )}
          {stats.by_status.sent > 0 && (
            <StatusRow
              label={getDeliveryStatusLabel(DeliveryStatus.SENT)}
              count={stats.by_status.sent}
              color="blue"
            />
          )}
          {stats.by_status.delivered > 0 && (
            <StatusRow
              label={getDeliveryStatusLabel(DeliveryStatus.DELIVERED)}
              count={stats.by_status.delivered}
              color="green"
            />
          )}
          {stats.by_status.read > 0 && (
            <StatusRow
              label={getDeliveryStatusLabel(DeliveryStatus.READ)}
              count={stats.by_status.read}
              color="purple"
            />
          )}
          {stats.by_status.failed > 0 && (
            <StatusRow
              label={getDeliveryStatusLabel(DeliveryStatus.FAILED)}
              count={stats.by_status.failed}
              color="red"
            />
          )}
        </div>
      </div>

      {/* Channel Breakdown */}
      <div>
        <h4 className="text-sm font-medium mb-3">По каналам</h4>
        <div className="flex flex-wrap gap-2">
          {stats.by_channel.push !== undefined && stats.by_channel.push > 0 && (
            <Badge variant="secondary">
              {getNotificationChannelLabel(NotificationChannel.PUSH)}: {stats.by_channel.push}
            </Badge>
          )}
          {stats.by_channel.email !== undefined && stats.by_channel.email > 0 && (
            <Badge variant="secondary">
              {getNotificationChannelLabel(NotificationChannel.EMAIL)}: {stats.by_channel.email}
            </Badge>
          )}
          {stats.by_channel.sms !== undefined && stats.by_channel.sms > 0 && (
            <Badge variant="secondary">
              {getNotificationChannelLabel(NotificationChannel.SMS)}: {stats.by_channel.sms}
            </Badge>
          )}
        </div>
      </div>
    </Card>
  )
}

interface StatusRowProps {
  label: string
  count: number
  color: 'gray' | 'blue' | 'green' | 'purple' | 'red'
}

function StatusRow({ label, count, color }: StatusRowProps) {
  const colorClasses = {
    gray: 'bg-gray-100 text-gray-800',
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    purple: 'bg-purple-100 text-purple-800',
    red: 'bg-red-100 text-red-800',
  }

  return (
    <div className="flex items-center justify-between py-2 px-3 bg-muted rounded">
      <span className="text-sm">{label}</span>
      <Badge className={colorClasses[color]}>{count}</Badge>
    </div>
  )
}
